import { useCallback, useState } from "react";
import type { MediaTab } from "./types";
import { ROOT_PLACEHOLDER } from "./types";
import { resolvePathValue } from "./utils";
import type { StorageUrlState, StorageUrlSync } from "./useStorageUrlSync";

const INITIAL_STATE: StorageUrlState = {
	path: ROOT_PLACEHOLDER,
	fileId: null,
	q: "",
	tab: "all",
	taxonomyIds: []
};

/**
 * Même contrat que useStorageUrlSync, mais adossé à un état local : pour le mode « pick »
 * (médiathèque montée dans une modale), où la navigation ne doit PAS toucher l'URL de la page
 * hôte. Les sémantiques sont identiques : changer de dossier ferme le détail et efface la
 * recherche, changer d'onglet revient à la racine.
 */
export const useLocalMediaNav = (initialPath?: string): StorageUrlSync => {
	const [urlState, setUrlState] = useState<StorageUrlState>(() =>
		initialPath ? { ...INITIAL_STATE, path: resolvePathValue(initialPath) } : INITIAL_STATE
	);

	const navigateToPath = useCallback((value: string, options?: { keepSearch?: boolean }) => {
		const normalized = value === ROOT_PLACEHOLDER ? ROOT_PLACEHOLDER : resolvePathValue(value);
		setUrlState((prev) => ({
			...prev,
			path: normalized,
			fileId: null,
			tab: "all",
			q: options?.keepSearch ? prev.q : ""
		}));
	}, []);

	const setTab = useCallback((tab: MediaTab) => {
		setUrlState((prev) => ({ ...prev, tab, path: ROOT_PLACEHOLDER, fileId: null }));
	}, []);

	const setSearch = useCallback((q: string) => {
		setUrlState((prev) => ({ ...prev, q: q.trim() }));
	}, []);

	const setTaxonomyIds = useCallback((ids: string[]) => {
		setUrlState((prev) => ({ ...prev, taxonomyIds: ids.map((id) => id.trim()).filter(Boolean) }));
	}, []);

	const selectFile = useCallback((fileId: string | null) => {
		setUrlState((prev) => ({ ...prev, fileId }));
	}, []);

	const showUploaded = useCallback((path: string | null | undefined, fileId: string | null | undefined) => {
		setUrlState((prev) => ({
			...prev,
			path: path ? resolvePathValue(path) : ROOT_PLACEHOLDER,
			fileId: fileId ?? null,
			tab: "all",
			q: ""
		}));
	}, []);

	return { urlState, navigateToPath, setTab, setSearch, setTaxonomyIds, selectFile, showUploaded };
};
